import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthLayout, ErrorAlert } from "../components/AuthLayout";
import { supabase } from "../lib/supabase";
import { OAUTH_MSG, OAUTH_ERR } from "../lib/authApi";
import { useNoindex } from "../hooks/useNoindex";

function notifyOpener(msg) {
  const opener = window.opener && !window.opener.closed ? window.opener : null;
  if (opener) opener.postMessage(msg, window.location.origin);
  try {
    const bc = new BroadcastChannel(OAUTH_MSG);
    bc.postMessage(msg);
    bc.close();
  } catch (e) {
    // BroadcastChannel unsupported
  }
  return !!opener;
}

export default function AuthCallback() {
  useNoindex();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const query = new URLSearchParams(window.location.search);
      const errMsg = hash.get("error_description") || query.get("error_description");
      if (errMsg) {
        notifyOpener({ type: OAUTH_ERR, message: errMsg });
        setError(errMsg);
        return;
      }
      if (query.get("code")) await supabase.auth.exchangeCodeForSession(query.get("code")).catch(() => null);
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) {
        setError("Sign-in could not be completed. Please try again.");
        return;
      }
      const isPopup = notifyOpener({ type: OAUTH_MSG, access_token: session.access_token, refresh_token: session.refresh_token });
      if (isPopup) {
        setDone(true);
        setTimeout(() => window.close(), 400);
      } else {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <AuthLayout testId="auth-callback-page" eyebrow="Sign-in" title={error ? "Sign-in failed" : "Signing you in"}>
      {error ? (
        <div className="space-y-4">
          <ErrorAlert message={error} testId="auth-callback-error" />
          <Link to="/login" className="block text-center text-sm font-semibold text-[#7A0016] hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <p data-testid="auth-callback-status" className="flex items-center gap-2 text-sm text-[#8C6A6C]">
          <Loader2 className="h-4 w-4 animate-spin text-[#7A0016]" />
          {done ? "Signed in. You can close this window." : "Completing sign-in..."}
        </p>
      )}
    </AuthLayout>
  );
}
